import DocumentViewerModal from "components/DocumentViewer";
import FileUpload from "components/FileUpload";
import FileView from "components/fileView";
import { StatusChip } from "components/StatusChip";
import TeamView from "components/team_view";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "react-i18next";
import { Leaf } from "lucide-react";
import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateInternalLegalResearchRequest, updateLegalResearchRequest } from "services/legalResearchService";
import { getResearchSchedules } from "services/researchScheduleService";
import {
    LEGAL_RESEARCH_REQUEST_STATUS
} from "utils/constantValues";
import { formatDate, formatDay, calculateRemainingDays } from "utils/helpers";
import ActionAvatars from "./actionAvatars";
import ManagementFeedback from "./managementFeedback";
import ResearchPresentationFeedback from "./researchPresentationFeedback";
import RespondBackForm from "./respondBackform";
import ScheduleDraftPresentationModal from "./scheduleDraftPresentationModal";
import SchedulePresentationModal from "./schedulePresentationModal";
import UploadResearchData from "./uploadResearchData";
import UploadResearchDraft from "./uploadResearchDraft";

import { expandRoles, isTeamLeader } from "utils/authRules";
interface Schedule {
    id: string;
    location: string;
    purpose: string;
    remark: string;
    internal_research_request: string;
    scheduled_datetime: string;
}

function InternalResearchDraft({ request }: { request: any }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const contextData = useAuth();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [selectedData, setSelectedData] = useState(0);
    const [schedules, setSchedules] = useState<Schedule | null>(null);
    const [isRespondBackFormOpen, setIsRespondBackFormOpen] = useState(false);
    const [proposalFile, setProposalFile] = useState(null);
    const [formState, setFormState] = useState({
        ...request,
        urgency_level: request.urgency_level
            ? request.urgency_level.charAt(0).toUpperCase() +
            request.urgency_level.slice(1).toLowerCase()
            : "High",
    });
    const [implementationDirection, setImplementationDirection] = useState(formState?.implementation_direction ?? "");

    const [viewTeam, setViewTeam] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalEditOpen, setIsModalEditOpen] = useState(false);
    const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [isFeedbackFormOpen, setIsFeedBackFormOpen] = useState(false);
    const [showDraftHistory, setShowDraftHistory] = useState(false);
    const [showDataHistory, setShowDataHistory] = useState(false);
    const [showAmendedFileHistory, setShowAmendedFileHistory] = useState(false);
    const [showFeedbackFileHistory, setShowFeedbackFileHistory] = useState(false);

    const handleRespondBackClick = () => {
        setIsRespondBackFormOpen(!isRespondBackFormOpen);
    }
    const handleScheduleResearchClick = () => {
        setIsScheduleModalOpen(true);
    };
    const handleScheduleCloseModal = () => {
        setIsScheduleModalOpen(false);
    };
    const handleUploadDraftClick = () => {
        setIsDraftModalOpen(true);
    };
    const handleDraftCloseModal = () => {
        setIsDraftModalOpen(false);
    };
    const handleUploadDataClick = () => {
        setIsModalOpen(true);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };
    const handleImplementationChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setImplementationDirection(event.target.value);
    };

    const handleResearchReject = async () => {
        const data = new FormData();
        data.append(
            "status",
            LEGAL_RESEARCH_REQUEST_STATUS.draft_rejected.value
        );

        try {
            const response = await updateLegalResearchRequest(formState.id, data, contextData.token);

            setFormState({
                ...response,
                urgency_level: response.urgency_level
                    ? response.urgency_level.charAt(0).toUpperCase() +
                    response.urgency_level.slice(1).toLowerCase()
                    : "High",
            });
            showToast(t("LegalResearchDraftRejected"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToRejectLegalResearchDraft"), "error");
        } finally {
            setLoading(false);
        }
    };

     const handleResearchApprove = async () => {
         setLoading(true);
         const data = new FormData();
         data.append(
             "forward_status",
             LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value
         );

         try {
             const response = await updateInternalLegalResearchRequest(contextData.token, formState.id, data);

             setFormState({
                 ...response,
                 urgency_level: response.urgency_level
                     ? response.urgency_level.charAt(0).toUpperCase() +
                       response.urgency_level.slice(1).toLowerCase()
                     : "High",
             });
             showToast(t("LegalResearchForwardedToDirectorSuccess"), "success");
         } catch (error) {
             console.error("Error submitting request:", error);
             showToast(t("FailedToForwardLegalResearchToDirector"), "error");
         } finally {
             setLoading(false);
         }
     };

     const handleDirectorApprove = async () => {
         setLoading(true);
         const data = new FormData();
         data.append(
             "status",
             LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value
         );
         data.append(
             "forward_status",
             LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value
         );

         try {
             const response = await updateInternalLegalResearchRequest(contextData.token, formState.id, data);

             setFormState({
                 ...response,
                 urgency_level: response.urgency_level
                     ? response.urgency_level.charAt(0).toUpperCase() +
                       response.urgency_level.slice(1).toLowerCase()
                     : "High",
             });
             showToast(t("LegalResearchApprovedByDirector"), "success");
         } catch (error) {
             console.error("Error approving request:", error);
             showToast(t("FailedToApproveLegalResearch"), "error");
         } finally {
             setLoading(false);
         }
     };

     const handleImplementationSubmit = async () => {
         setLoading(true);
         const data = new FormData();

         data.append("implementation_direction", implementationDirection);
         if (proposalFile)
             data.append("implementation_direction_file", proposalFile);

         try {
             const response = await updateInternalLegalResearchRequest(contextData.token, formState.id, data);

             setFormState((prev: any) => ({
                 ...prev, ...response,
                 urgency_level: response.urgency_level
                     ? response.urgency_level.charAt(0).toUpperCase() +
                       response.urgency_level.slice(1).toLowerCase()
                     : "High",
             }));
             showToast(t("LegalResearchDraftApproved"), "success");
         } catch (error) {
             console.error("Error submitting request:", error);
             showToast(t("FailedToApproveLegalResearchDraft"), "error");
         } finally {
             setLoading(false);
         }
     };

    const handleForward = async (froward_status: string) => {
        const data = new FormData();
        data.append(
            "forward_status",
            froward_status
        );

        try {
            const response = await updateInternalLegalResearchRequest(contextData.token, formState.id, data);

            setFormState((prev: any) => ({
                ...prev, ...response,
                urgency_level: response.urgency_level
                    ? response.urgency_level.charAt(0).toUpperCase() +
                    response.urgency_level.slice(1).toLowerCase()
                    : "High",
            }));
            showToast(t("LegalResearchForwardedSuccess"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (
        file: any,
    ) => {
        setProposalFile(file);
    };

    useEffect(() => {
        getResearchSchedules(contextData.token)
            .then((data) => {
                const filtered = data.filter(
                    (schedule: any) => (schedule.internal_research_request === formState.id && schedule.purpose === "draft_presentation")
                );
                setSchedules(filtered[0]);
                console.log("draft filtered data:", filtered[0]);
            })
            .catch((error) => {
                console.error("Error fetching legal research request:", error);
                showToast(t("FailedToFetchLegalResearchRequest"), "error");
            });
    }, [formState, isScheduleModalOpen]);

    useEffect(() => { }, [formState]);

    return (
        <div className="h-full overflow-scroll scrollbar-hide">
            {schedules &&
                (formState?.research_presentation_summary ||
                    formState?.documents?.find((document: any) => typeof document.document_type === "string" && (document.document_type.includes("draft_feedback") || document.document_type.includes("amended")))) &&
                <div className="flex flex-col bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6 max-w-4xl hover:shadow-xl transition-all duration-300">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    {t("ResearchPresentationFeedback")}
                                </span>
                            </div>
                            {formState?.internal_topic_schdule?.find((schedule: any) => schedule.purpose === "draft_presentation") && (
                                <p className="text-[#B8B8B8] font-regular text-sm mb-2">
                                    {formatDate(formState?.internal_topic_schdule?.find((schedule: any) => schedule.purpose === "draft_presentation")?.scheduled_datetime)}
                                </p>
                            )}
                        </div>
                        {(formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value &&
                            formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value) &&
                            formState.implementation_direction &&
                            (contextData?.user?.roles?.some((p) => p === "OAGBManagementHead") ||
                                contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
                                contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) &&
                            <button onClick={() => handleForward(LEGAL_RESEARCH_REQUEST_STATUS.sent_to_registrar.value)} type="button" className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg py-2 px-4 font-semibold shadow-md transition-all duration-200 flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                                </svg>
                                {t("ForwardToRegistrar")}
                            </button>
                        }
                        {/* Prosecutor forwards the presentation feedback to the coordinator first */}
                        {(formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value &&
                            formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value) &&
                            (expandRoles(contextData?.user?.roles || []).some((p) => p === "LegalResearchProsecutor") && isTeamLeader(formState?.internal_topic_assignment, contextData?.user)) &&
                            formState.research_presentation_summary &&
                            <button onClick={() => handleForward(LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value)} type="button" className="bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white rounded-lg py-2 px-4 font-medium shadow-md transition-all duration-200 flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                                {t("ForwardToCoordinator")}
                            </button>
                        }
                        {/* Coordinator or Director forwards to management */}
                        {(formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value &&
                            formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value) &&
                            (contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
                                contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) &&
                            <button onClick={() => handleForward(LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value)} type="button" className="bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white rounded-lg py-2 px-4 font-medium shadow-md transition-all duration-200 flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                                {t("ForwardToManagement")}
                            </button>
                        }
                    </div>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <p className="text-[#606060] font-regular text-base leading-relaxed">{formState?.research_presentation_summary}</p>
                    </div>
                    {/* Presentation feedback file */}
                    {formState?.documents?.find((document: any) => typeof document.document_type === "string" && document.document_type.includes("draft_feedback")) ? (
                        <div className="mt-4">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-2">{t("PresentationFeedback") || t("ResearchFeedbackFile")}</p>
                            <DocumentViewerModal
                                documents={formState?.documents.filter((document: any) => typeof document.document_type === "string" && document.document_type.includes("draft_feedback"))}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/internal-legal-research-documents/document`}
                            />
                        </div>
                    ) : formState?.research_draft_feedback ? (
                        <div className="mt-4">
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <FileView title={t("ResearchFeedbackFile")} file={formState?.research_draft_feedback} />
                            </div>
                        </div>
                    ) : null}
                    {/* Amended research file — green-accented card */}
                    {formState?.documents?.find((document: any) => typeof document.document_type === "string" && document.document_type.includes("amended")) &&
                        <div className="mt-4 border-2 border-green-500 bg-green-50 rounded-lg p-4">
                            <p className="text-[11px] uppercase tracking-wide text-green-700 font-semibold mb-2">{t("ResearchAmendedFile")}</p>
                            <DocumentViewerModal
                                documents={formState?.documents.filter((document: any) => typeof document.document_type === "string" && document.document_type.includes("amended"))}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/internal-legal-research-documents/document`}
                            />
                        </div>
                    }
                    {/* Research synopsis */}
                    {formState?.documents?.find((document: any) => typeof document.document_type === "string" && document.document_type.includes("synopsis")) &&
                        <div className="mt-4">
                            <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-2">{t("ResearchSynopsis")}</p>
                            <DocumentViewerModal
                                documents={formState?.documents.filter((document: any) => typeof document.document_type === "string" && document.document_type.includes("synopsis"))}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/internal-legal-research-documents/document`}
                            />
                        </div>
                    }
                    {/* Implementation Direction (read-only) once set */}
                    {formState?.implementation_direction &&
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <p className="text-base font-semibold text-[#212121] mb-2">{t("ImplementationDirection")}</p>
                            <p className="text-[#606060] font-regular text-base leading-relaxed whitespace-pre-line">
                                {formState.implementation_direction}
                            </p>
                            {formState?.documents?.find((document: any) => typeof document.document_type === "string" && document.document_type.includes("implementation_direction")) &&
                                <div className="mt-3">
                                    <DocumentViewerModal
                                        documents={formState?.documents.filter((document: any) => typeof document.document_type === "string" && document.document_type.includes("implementation_direction"))}
                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/internal-legal-research-documents/document`}
                                    />
                                </div>
                            }
                        </div>
                    }
                    {contextData?.user?.roles?.some((p) => p === "OAGBManagementHead") &&
                        !formState?.implementation_direction &&
                        <form className="mt-6 pt-6 border-t border-gray-200">
                            <label
                                htmlFor="remark"
                                className="mb-3 block text-xl font-semibold text-[#212121]"
                            >
                                {t("ImplementationDirection")}
                            </label>
                            <textarea
                                id="remark"
                                className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-4 focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-28 resize-y transition-all duration-200 hover:border-gray-400"
                                placeholder={t("DetailHere")}
                                required
                                value={implementationDirection}
                                onChange={handleImplementationChange}
                            ></textarea>
                            <div className="mt-4">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                            <button type="button" onClick={handleImplementationSubmit} className="py-4 mt-4 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white hover:bg-[#111111] rounded-lg flex-1 w-full h-fit font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center" disabled={loading}>
                                {loading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t("Submitting")}
                                    </>
                                ) : t("SubmitImplementationDirection")}
                            </button>
                        </form>}
                </div>}
            <div className="flex flex-col max-w-4xl bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300">
                {schedules &&
                    (<div onClick={() => (formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value ||
                                         contextData?.user?.roles?.some((p: string) => p === "GeneralAttorney")) ?
                        setIsScheduleModalOpen(!isScheduleModalOpen) : setIsScheduleModalOpen(false)}
                        className="border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-5 cursor-pointer hover:shadow-md transition-shadow">
                        {/* Official header */}
                        <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-3 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                                        {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
                                    </p>
                                    <h3 className="text-white font-bold text-base truncate">{t("ResearchDraftPresentation")}</h3>
                                </div>
                            </div>
                            {schedules?.scheduled_datetime &&
                                (() => {
                                    const days = calculateRemainingDays(schedules.scheduled_datetime);
                                    const label =
                                        days < 0
                                            ? t("OverdueByDays", { count: Math.abs(days) })
                                            : days === 0
                                                ? t("Today")
                                                : t("DaysLeft", { count: days });
                                    const cls =
                                        days < 0
                                            ? "bg-red-100 text-red-700"
                                            : days === 0
                                                ? "bg-orange-100 text-orange-700"
                                                : days <= 3
                                                    ? "bg-yellow-100 text-yellow-700"
                                                    : "bg-green-100 text-green-700";
                                    return (
                                        <span className={`flex-shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${cls}`}>
                                            {label}
                                        </span>
                                    );
                                })()}
                        </div>
                        {/* Body */}
                        <div className="p-4 bg-white flex items-center justify-between gap-4">
                            <div>
                                <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-0.5">{t("Schedule") || "Date"}</p>
                                {schedules?.scheduled_datetime ? (
                                    <p className="text-sm font-semibold text-[#0A1D39]">
                                        {formatDay(schedules?.scheduled_datetime).weekday}, {formatDay(schedules.scheduled_datetime).rest}
                                    </p>
                                ) : (
                                    <p className="text-sm text-gray-400">{t("DateNotScheduled")}</p>
                                )}
                            </div>
                            {schedules?.location && (
                                <div className="text-right">
                                    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-0.5">{t("Location")}</p>
                                    <p className="text-sm font-semibold text-[#0A1D39]">📍 {schedules?.location}</p>
                                </div>
                            )}
                        </div>
                        {/* Forward to Prosecutor — head, after scheduling */}
                        {formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value &&
                            formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
                            contextData?.user?.roles?.some((p) => p === "GeneralAttorney") && (
                                <div className="px-4 pb-4 bg-white">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleForward(LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value); }}
                                        type="button"
                                        className="w-full flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors"
                                    >
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                        {t("ForwardToProsecutor")}
                                    </button>
                                </div>
                            )}
                    </div>)}
                <div className="pb-2">
                    <div className="flex">
                        {formState?.draft_summary &&
                            <div className="w-full">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="font-bold text-lg text-[#212121]">{t("ResearchDraftSummary")}</p>
                                    {(formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value &&
                                        formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value) &&
                                        (expandRoles(contextData?.user?.roles || []).some((p) => p === "LegalResearchProsecutor") && isTeamLeader(formState?.internal_topic_assignment, contextData?.user)) &&
                                        <button onClick={() => handleForward(LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value)} type="button" className="bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white rounded-lg py-2 px-5 font-medium shadow-md transition-all duration-200 flex items-center gap-2 text-sm">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                            {t("ForwardToCoordinator")}
                                        </button>
                                    }
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                    <p className="text-[#606060] font-regular text-base leading-relaxed">{formState?.draft_summary}</p>
                                </div>
                            </div>
                        }
                    </div>
                    {/* Latest draft file shown; previous ones in an accordion */}
                    {(() => {
                        const internalBase = `${process.env.REACT_APP_API_BASE_URL}legal_research/internal-legal-research-documents/document`;
                        const draftDocs = (formState?.documents ?? [])
                            .filter((d: any) => d.document_type === "internal_draft" || d.document_type === "research_draft_file")
                            .slice()
                            .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                        const draftHistories = (formState?.internal_topic_files ?? [])
                            .filter((fh: any) => fh.file_type === "draft")
                            .slice()
                            .reverse();
                        if (draftDocs.length === 0 && !formState?.research_draft_file && draftHistories.length === 0)
                            return null;
                        const [latest, ...previousDocs] = draftDocs;
                        const previousCount = previousDocs.length + draftHistories.length;
                        return (
                            <div className="mt-4">
                                {latest ? (
                                    <DocumentViewerModal documents={[latest]} baseUrl={internalBase} />
                                ) : formState?.research_draft_file ? (
                                    <FileView title={t("ResearchDraft")} file={formState?.research_draft_file} />
                                ) : null}
                                {previousCount > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowDraftHistory(!showDraftHistory)}
                                        className="mt-3 flex items-center gap-1.5 text-sm font-medium text-[#0A1D39] hover:underline"
                                    >
                                        <svg className={`h-3 w-3 transition-transform ${showDraftHistory ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 8" fill="currentColor">
                                            <path d="M5 8L0.669872 0.499999L9.33013 0.5L5 8Z" />
                                        </svg>
                                        {t("PreviousDraftFiles") || "Previous draft files"} ({previousCount})
                                    </button>
                                )}
                                {showDraftHistory && previousCount > 0 && (
                                    <div className="border border-gray-300 p-5 mt-3 rounded-lg space-y-4 bg-gray-50">
                                        {previousDocs.map((doc: any, i: number) => (
                                            <DocumentViewerModal key={doc.uu_id || doc.id || i} documents={[doc]} baseUrl={internalBase} />
                                        ))}
                                        {draftHistories.map((fileHistroy: any, i: number) => (
                                            <div key={`fh-${i}`} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                                                <FileView title={t("Draft")} file={fileHistroy?.file}
                                                    fileName={fileHistroy?.file &&
                                                        fileHistroy?.file.substring(
                                                            fileHistroy?.file.lastIndexOf("/") + 1
                                                        )} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
                {formState?.internal_data_files?.length > 0 &&
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-4 mt-6">
                        {/* Header */}
                        <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b border-gray-200">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-lg bg-[#0A1D39] flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-wider text-[#9B0505] font-semibold">
                                        {t("OfficeOfTheAttorneyGeneral") ||
                                            "Office of the Attorney General"}
                                    </p>
                                    <h3 className="font-bold text-[#0A1D39] text-base truncate">
                                        {t("ResearchData")}
                                        {formState?.internal_data_files?.[selectedData]?.data_name
                                            ? ` — ${formState?.internal_data_files?.[selectedData]?.data_name}`
                                            : ""}
                                    </h3>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {formState?.internal_data_files?.length > 1 &&
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowDataHistory(!showDataHistory)}
                                            className="flex items-center gap-1.5 text-sm font-medium text-[#0A1D39] border border-[#0A1D39] rounded-lg px-3 py-1.5 hover:bg-[#0A1D39]/5 transition-colors"
                                        >
                                            <span>{t("ViewResearchData")}</span>
                                            <svg className={`w-4 h-4 transition-transform duration-200 ${showDataHistory ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        {showDataHistory && (
                                            <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden min-w-[200px]">
                                                {formState?.internal_data_files?.map((entry: any, idx: number) => (
                                                    <button
                                                        onClick={() => { setSelectedData(idx); setShowDataHistory(false); }}
                                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${selectedData === idx ? 'bg-[#0A1D39]/5 text-[#0A1D39] font-semibold' : 'text-gray-700'}`}
                                                        key={idx}
                                                    >
                                                        {entry?.data_name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                }
                                {expandRoles(contextData?.user?.roles || []).some((p) => p === "LegalResearchProsecutor") &&
                                    formState?.internal_topic_assignment?.find((pr: any) => pr.team_type === "prosecutor_research_team" && pr.prosecutor_user_id === contextData?.user?.user_id)?.is_team_leader && (
                                        <>
                                            <button onClick={() => setIsModalEditOpen(true)} className="flex items-center gap-1.5 text-sm font-medium text-[#0A1D39] border border-[#0A1D39] rounded-lg px-3 py-1.5 hover:bg-[#0A1D39]/5 transition-colors">
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                                {t("Edit")}
                                            </button>
                                        </>
                                    )}
                            </div>
                        </div>
                        {/* Summary */}
                        {formState?.internal_data_files?.[selectedData]?.data_summary && (
                            <p className="text-sm text-gray-600 leading-relaxed mb-3">
                                {formState?.internal_data_files?.[selectedData]?.data_summary}
                            </p>
                        )}
                        {/* Documents */}
                        {formState?.internal_data_files?.[selectedData]?.research_data_documents && formState.internal_data_files[selectedData].research_data_documents.length > 0 ? (
                            <DocumentViewerModal
                                documents={formState?.internal_data_files?.[selectedData]?.research_data_documents}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/research-data-file-documents/document/`}
                            />
                        ) : (
                            <FileView title={t("ResearchData")} file={formState?.internal_data_files?.[selectedData]?.datafile} />
                        )}
                        {/* Research Data Collectors — moved inside the Research Data card */}
                        {formState?.internal_data_coolection?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200 flex items-start">
                                <div className="w-full">
                                    <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-2">{t("ResearchDataCollectors")}</p>
                                    <ActionAvatars
                                        title={t("GatheredBy")}
                                        users={formState.internal_data_coolection?.filter((collector: any) => collector.research_data_file === formState?.internal_data_files?.[selectedData]?.id)}
                                        prosecutor={false}
                                    />
                                </div>
                                {expandRoles(contextData?.user?.roles || []).some((p) => p === "LegalResearchProsecutor") &&
                                    formState?.internal_topic_assignment?.find((prosecutor: any) => prosecutor.prosecutor_user_id === contextData?.user?.user_id)?.is_team_leader &&
                                    <button onClick={() => setIsModalEditOpen(true)} className="my-auto ml-2 p-2 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors duration-200" title={t("EditTeam")}>
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="#4B5563" />
                                        </svg>
                                    </button>}
                                <button onClick={() => setViewTeam(!viewTeam)} className="mr-2 ml-auto my-auto cursor-pointer p-2 hover:bg-gray-200 rounded-lg transition-colors duration-200" title={t("ViewTeam")}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <mask id="mask0_9_31087" maskUnits="userSpaceOnUse" x="1" y="5" width="22" height="14">
                                            <path d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z" fill="white" stroke="white" stroke-width="1.33333" stroke-linejoin="round" />
                                            <path d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z" fill="black" stroke="black" stroke-width="1.33333" stroke-linejoin="round" />
                                        </mask>
                                        <g mask="url(#mask0_9_31087)">
                                            <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                                        </g>
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>}
                {((contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") || contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) &&
                    formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
                    (formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value || formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value)) &&
                    <>
                        {
                            (contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
                                contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
                                contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) &&
                            (<RespondBackForm
                                request={formState}
                                setLegalResearch={setFormState}
                                isOpen={isRespondBackFormOpen}
                                onClose={() => setIsRespondBackFormOpen(false)}
                                forward={LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value}
                                status={LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value}
                            />)
                        }
                        {contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ? (
                            <div className="mt-6 flex gap-3 font-medium text-base">
                                <button onClick={handleRespondBackClick} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-base py-3 px-8 rounded-lg ml-6 font-semibold transition-all duration-200 flex items-center justify-center gap-2" >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                    </svg>
                                    {t("RespondBack")}
                                </button>
                                <button onClick={handleResearchApprove} className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t("Forwarding")}
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            {t("ForwardToDirector")}
                                        </>
                                    )}
                                </button>
                            </div>
                        ) : contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") ? (
                            <div className="mt-6 flex gap-3 font-medium text-base">
                                <button onClick={handleRespondBackClick} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-base py-3 px-8 rounded-lg ml-6 font-semibold transition-all duration-200 flex items-center justify-center gap-2" >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                    </svg>
                                    {t("RespondBack")}
                                </button>
                                <button onClick={handleDirectorApprove} className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t("Approving")}
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            {t("Approve")}
                                        </>
                                    )}
                                </button>
                            </div>
                        ) : null}
                    </>
                }
                {/* Director approval section - when forwarded to director */}
                {(contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") &&
                    formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value &&
                    (formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value || 
                     formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value ||
                     formState.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value)) &&
                    <>
                        {
                            (<RespondBackForm
                                request={formState}
                                setLegalResearch={setFormState}
                                isOpen={isRespondBackFormOpen}
                                onClose={() => setIsRespondBackFormOpen(false)}
                                forward={LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value}
                                status={LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value}
                            />)
                        }
                        <div className="mt-6 flex gap-3 font-medium text-base">
                            <button onClick={handleRespondBackClick} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-base py-3 px-8 rounded-lg ml-6 font-semibold transition-all duration-200 flex items-center justify-center gap-2" >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                </svg>
                                {t("RespondBack")}
                            </button>
                            <button onClick={handleDirectorApprove} className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" disabled={loading}>
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t("Approving")}
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t("ApproveAndForwardToHead")}
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                }
                {(contextData?.user?.roles?.some((p) => p === "GeneralAttorney") &&
                    formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_approved.value) &&
                    !schedules &&
                    <>
                        {
                            (contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
                                contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
                                contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) &&
                            (<RespondBackForm
                                request={formState}
                                setLegalResearch={setFormState}
                                isOpen={isRespondBackFormOpen}
                                onClose={() => setIsRespondBackFormOpen(false)}
                                forward={LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value}
                                status={LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value}
                            />)
                        }
                        <div className="mt-6 flex gap-3 font-medium text-base mb-6">
                            <button onClick={handleRespondBackClick} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-base py-3 px-8 rounded-lg ml-6 font-semibold transition-all duration-200 flex items-center justify-center gap-2" >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                </svg>
                                {t("RespondBack")}
                            </button>
                            <button onClick={handleScheduleResearchClick} className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {t("ScheduleResearch")}
                            </button>
                        </div>
                    </>
                }
                {schedules && expandRoles(contextData?.user?.roles || []).some((p) => p === "LegalResearchProsecutor") &&
                    formState.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
                    formState?.internal_topic_assignment?.find((prosecutor: any) => prosecutor.prosecutor_user_id === contextData?.user?.user_id)?.is_team_leader &&
                    <div className="mt-4 flex gap-2 font-medium text-base">
                        <button onClick={() => { setIsFeedBackFormOpen(!isFeedbackFormOpen) }}
                            className="py-4 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black text-white hover:bg-red-700 rounded-lg flex-1 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
                            {formState?.research_presentation_summary && <span className="mr-1">{t("Re")}</span>}
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            {t("UploadPresentationFeedback")}
                        </button>
                    </div>}
            </div>

            {(expandRoles(contextData?.user?.roles || []).some((p) => p === "LegalResearchProsecutor") &&
                formState?.forward_status === LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
                formState?.internal_topic_assignment?.find((prosecutor: any) => prosecutor.team_type === "prosecutor_research_team" &&
                    prosecutor.prosecutor_user_id === contextData?.user?.user_id)?.is_team_leader &&
                (formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.research_underway.value ||
                    formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value ||
                    formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.draft_revision.value
                )) &&
                <div className="mt-6">
                    <div className="flex items-start gap-2 bg-[#0A1D39]/5 border border-[#0A1D39]/20 rounded-lg p-3 mb-3">
                        <svg className="w-5 h-5 text-[#0A1D39] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-[#0A1D39] leading-relaxed">
                            {t("ProsecutorDraftInstruction") ||
                                "As the team leader, insert the collected research data, then upload the research draft for review."}
                        </p>
                    </div>
                    <div className="flex gap-3 font-medium text-base">
                        <button onClick={handleUploadDataClick} className="py-3 bg-white text-[#0A1D39] border border-[#0A1D39] hover:bg-[#0A1D39]/5 rounded-lg flex-1 font-semibold transition-colors flex items-center justify-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {t("InsertData")}
                        </button>
                        <button onClick={handleUploadDraftClick} className="py-3 bg-[#0A1D39] hover:bg-[#13294d] text-white rounded-lg flex-1 font-semibold shadow-sm transition-colors flex items-center justify-center gap-2">
                            {formState?.research_draft_file && <span className="mr-1">{t("Re")}</span>}
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            {t("UploadResearchDraft")}
                        </button>
                    </div>
                </div>}
            <UploadResearchData
                request={formState}
                setLegalResearch={setFormState}
                isOpen={isModalEditOpen}
                onClose={() => setIsModalEditOpen(false)}
                researchData={formState.internal_data_files?.[selectedData]}
            />
            <UploadResearchData
                request={formState}
                setLegalResearch={setFormState}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
            <UploadResearchDraft
                request={formState}
                setLegalResearch={setFormState}
                isOpen={isDraftModalOpen}
                onClose={handleDraftCloseModal}
            />
            <ScheduleDraftPresentationModal
                data={formState}
                setLegalResearch={setSchedules}
                isOpen={isScheduleModalOpen}
                onClose={handleScheduleCloseModal}
            />
            {(isFeedbackFormOpen && expandRoles(contextData?.user?.roles || []).some((p) => p === "LegalResearchProsecutor")) &&
                <ResearchPresentationFeedback
                    request={formState}
                    setLegalResearch={setFormState}
                    isOpen={isFeedbackFormOpen}
                    onClose={() => setIsFeedBackFormOpen(false)}
                />
            }
            {viewTeam &&
                <TeamView
                    teamMembers={(formState?.internal_data_coolection ?? []).filter((collector: any) => collector.research_data_file === formState?.internal_data_files?.[selectedData]?.id)}
                    onClose={() => setViewTeam(false)} />}
        </div>
    );
}

export default InternalResearchDraft;